import React from 'react'
import { Skeleton, Form, Input, InputNumber, Select, Button, Typography, Row, Col, Card, Divider, message } from 'antd'
import { FormComponent, BaseFormComponentProps } from '../../../BaseComponent'
import CheckoutProductList from '../../../CheckoutProductList'
import { AdoptionApplicationProps } from '..'
import "./style.css"
export default class Checkout extends FormComponent<AdoptionApplicationProps & BaseFormComponentProps> {

  state = {
    saving: false,
    extraValueType: ''
  }

  formRef = null as any

  componentDidMount() {
    const { data } = this.props

    // Validate permissions
    this.validateAccessPermissions()

    if (data?.checkout) {
      const { checkout } = data

      const hasDiscount = checkout.find(p => p.type_key === 'DISCOUNT')
      const hasAddition = checkout.find(p => p.type_key === 'ADDITION')

      if (hasDiscount || hasAddition) {
        this.formRef.setFieldsValue({
          extra_value_type: hasDiscount ? 'discount' : 'addition'
        })
        this.handleExtraValueTypeChange(hasDiscount ? 'discount' : 'addition')
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const wasLoading = prevProps.loading && !this.props.loading

    // Validate permissions
    this.validateAccessPermissions()

    if (prevState.saving && wasLoading) {
      this.setState({
        saving: false
      }, () => {
        if (!this.props.error) {
          message.success('Adoption Application saved successfully!')
        } else {
          message.error(`Could not save checkout. Error: ${this.props.error}`)
        }
      })
    }
  }

  handleExtraValueTypeChange = (extraValueType: string) => {
    // Reset fields
    if (this.formRef) {
      const { data } = this.props

      this.formRef.setFieldsValue({
        discount: data?.checkout?.find(p => p.type_key === 'DISCOUNT')?.price || undefined,
        addition: data?.checkout?.find(p => p.type_key === 'ADDITION')?.price || undefined
      })
    }
    
    this.setState({
      extraValueType
    })
  }

  handleSave = (values) => {

    const { uuid, loading, adoptionApplicationCheckoutUpdate } = this.props

    if(this.props.data?.pet?.price && this.props.data?.pet?.price < Math.abs(values.discount)){
      message.error(`The discount cannot be greater than the price`)
      return null;
    }

    if (!loading) {
      const payload = {
        application_uuid: uuid,
        discount: Number(values.discount) || null,
        addition: Number(values.addition) || null,
        license: Number(values.license) || null,
        spay_neuter: Number(values.spay_neuter) || null
      }

      adoptionApplicationCheckoutUpdate(payload)
      this.setState({
        saving: true
      })
    }
  }

  _renderExtraValue = () => {
    const { extraValueType } = this.state

    switch(extraValueType) {
      case 'discount':
        return (
          <Form.Item
            name="discount"
            rules={[
              {
                required: true,
                message: 'Required value'
              },
              () => ({
                validator(_, value) {
                  if (!value || value < 0) {
                    return Promise.resolve();
                  }
                  return Promise.reject('Must be a negative number');
                },
              })
            ]}
          >
            <InputNumber
              max={0}
              formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value: any) => value.replace(/\$\s?|(,*)/g, '')}
              onChange={() => this.forceUpdate()}
            />
          </Form.Item>
        )
      case 'addition':
        return (
          <Form.Item
            name="addition"
            rules={[
              {
                required: true,
                message: 'Required value'
              },
              () => ({
                validator(_, value) {
                  if (!value || value > 0) {
                    return Promise.resolve();
                  }
                  return Promise.reject('Must be a positive number');
                },
              })
            ]}
          >
            <InputNumber
              min={0}
              formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value: any) => value.replace(/\$\s?|(,*)/g, '')}
              onChange={() => this.forceUpdate()}
            />
          </Form.Item>
        )
      default:
        return null
    }
  }

  render() {
    const { loading, permissions, history, uuid, data, adoptionApplicationCheckoutRemoveProduct } = this.props
    const { extraValueType } = this.state
    
    // Grab products from json
    const products = data?.checkout?.filter(checkout => checkout.type_key === 'PRODUCT').map(checkout => {
      let json = null
      try {
        json = JSON.parse(checkout.json as any)
      } catch {}

      return json
    }) || []

    // Grab checkout fields
    let values = {}
    data?.checkout?.forEach(checkout => {
      values[checkout.type_key.toLowerCase()] = Number(checkout.price)
    })

    // Card checkout info
    const merged = data?.checkout?.map(checkout => {
      return {
        type_key: checkout.type_key,
        price: Number(checkout.price)
      }
    }) || []

    // Add form edited fields
    if (this.formRef) {
      const fields = this.formRef.getFieldsValue()

      Object.keys(fields).forEach(k => {
        const field = Number(fields[k])
        const exists = merged.map(p => p.type_key).includes(k.toUpperCase())

        if (!k.includes('extra_') && !isNaN(field) && (field !== 0.0 || exists)) {
          const index = merged.findIndex(p => p.type_key.toUpperCase() === k.toUpperCase())
          if (index === -1) {
            merged.push({
              type_key: k.toUpperCase(),
              price: field
            })
          } else {
            merged[index].price = field
          }
        }
      })
    }

    const checkoutInfo = (
      <>
        {
          merged.map((checkout, index) => {
            const isOverwriting = data?.checkout?.filter(p => ['DISCOUNT', 'ADDITION'].includes(p.type_key)).find(p => p.type_key === checkout.type_key && checkout.type_key !== extraValueType.toUpperCase()) !== undefined
            const isRemoving = !isOverwriting && data?.checkout?.find(p => p.type_key === checkout.type_key && Number(checkout.price) === 0.0) !== undefined
            const isNew = !data?.checkout?.map(p => p.type_key).includes(checkout.type_key) && Number(checkout.price) !== 0.0
            const isChanged = !isNew && !isOverwriting && !isRemoving && data?.checkout?.find(p => p.type_key === checkout.type_key && Number(p.price) !== Number(checkout.price)) !== undefined

            return (
              <div key={`checkout-info-${index}`}>
                <Typography.Text type={isNew ? 'success' : undefined} strong delete={isOverwriting || isRemoving} mark={isChanged}>{checkout.type_key}: </Typography.Text>
                <Typography.Text type={isNew ? 'success' : undefined} delete={isOverwriting || isRemoving} mark={isChanged}>$ {checkout.price.toFixed(2)}</Typography.Text>
              </div>
            )
          })
        }
      </>
    )

    return (
      <div>
        <div className='checkout-row'>
          <div className='checkout-col' >
            <Typography.Title level={5}>Checkout</Typography.Title>

            <Form
              name="checkout"
              layout="vertical"
              ref={(refs: any) => this.formRef = refs}
              initialValues={values}
              onFinish={this.handleSave}
            >
              <Row  gutter={24} justify="space-between">
                <Col xs={24} md={12}>
                  <Form.Item label="Provide Discount | Increase Adoption Fee">
                    <Input.Group compact>
                      <Form.Item
                        name="extra_value_type"
                        noStyle
                      >
                        <Select
                          placeholder="Select an option"
                          onChange={this.handleExtraValueTypeChange}
                          style={{ width: '50%' }}
                        >
                          <Select.Option value="none">None</Select.Option>
                          <Select.Option value="discount">Discount</Select.Option>
                          <Select.Option value="addition">Increase Adoption Fee</Select.Option>
                        </Select>
                      </Form.Item>

                      {
                        this._renderExtraValue()
                      }
                    </Input.Group>
                  </Form.Item>
                </Col>

                <Col xs={24} md={4}>
                  <Form.Item
                    label="License"
                    name="license"
                  >
                    <InputNumber
                      min={0}
                      formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value: any) => value.replace(/\$\s?|(,*)/g, '')}
                      onChange={() => this.forceUpdate()}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={4}>
                  <Form.Item
                    label="Spay/Neuter"
                    name="spay_neuter"
                  >
                    <InputNumber
                      min={0}
                      formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value: any) => value.replace(/\$\s?|(,*)/g, '')}
                      onChange={() => this.forceUpdate()}
                    />
                  </Form.Item>
                </Col>
              </Row>
              
              <Divider />

              <Card
                title="Preview"
                className='checkout-preview'
                style={{marginBottom: 15}}
              >
                {
                  loading ? <Skeleton /> : (
                    <Card.Meta
                      description={checkoutInfo}
                    />
                  )
                }
              </Card>

              <Button className='checkout-button' type="primary" htmlType="submit" loading={loading} ref={(refs) => this.actionRefs.save = refs} >
                Save
              </Button>
            </Form>
          </div>
          <div className='checkout-col-2' >
            <Typography.Title level={5}>Products</Typography.Title>

            <CheckoutProductList
              application_uuid={uuid}
              products={products}
              permissions={permissions}
              history={history}
              loading={loading}

              adoptionApplicationCheckoutRemoveProduct={adoptionApplicationCheckoutRemoveProduct}
            />
          </div>
        </div>
      </div>
    )
  }
}
